import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";

import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles, productImageStyles } from "@insite/content-library/Components/ProductImage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { ProductSelectionPartsModel } from "@tCooneyBrothers/ApiModels";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
});

const mapDispatchToProps = {
    updateProductInfo,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    productSelectionPartsModel: ProductSelectionPartsModel;
    extendedStyles?: SparePartProductStyles;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface SparePartProductStyles {
    productImageStyles?: ProductImageStyles;
    productBrandWrapper?: InjectableCss;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPriceWrapper?: InjectableCss;
    dataTableRow?: DataTableRowProps;
    submitButton?: InjectableCss;
    description?: InjectableCss;
    productImageWrapper?: InjectableCss;
    spareMobileStyleComponent?: InjectableCss;
    hiddenSpareMobileStyleComponent?: InjectableCss;
}

productImageStyles.linkWrappingImage = {
    css: css`
        background: #fff;
        justify-content: center;
        div {
            width: 100%;
        }
        p[class^="TypographyStyle"] {
            max-height: 150px;
            overflow: hidden;
            font-size: 12px;
        }
    `,
};

export const sparePartProductStyles: SparePartProductStyles = {
    productBrandWrapper: {
        css: css`
            margin: 6px 0;
            min-height: 22px;
            padding: 5px 15px;
        `,
    },
    productDescriptionStyles: {
        productDetailLink: {
            css: css`
                margin-bottom: 6px;
                padding: 0;
            `,
        },
    },
    productPriceWrapper: {
        css: css`
            flex-grow: 1;
            margin-bottom: 6px;
            padding: 5px 15px;
            & + button[data-test-selector="addToListLink"] {
                justify-content: flex-end;
                position: absolute;
                bottom: 38px;
                right: 0;
            }
            section {
                legend {
                    font-size: 12px;
                    font-weight: 500;
                    margin-right: 5px;
                    & + div {
                        display: flex;
                    }
                }
                [data-test-selector="productPrice_unitNetPrice"] {
                    color: #2e64b0;
                    font-size: 12px;
                    font-weight: 700;
                }
                [data-test-selector="productPrice_unitOfMeasureLabel"] {
                    color: #2e64b0;
                    font-size: 11px;
                    font-weight: 700;
                }
            }
        `,
    },
    submitButton: {
        css: css`
            margin: 0px 0;
            cursor: pointer;
            font-family: inherit;
            line-height: 1;
            backface-visibility: hidden;
            transition: all 200ms ease-in-out 0s;
            padding: 0px 21px;
            background: rgb(88, 180, 108);
            border: 2px solid rgb(88, 180, 108);
            color: rgb(255, 255, 255) !important;
            width: 100%;
            height: 40px;
            max-width: 250px;
            margin-bottom: 10px;
            > span[class^="TypographyStyle"] {
                color: #fff !important;
                text-align: center;
                margin: 0 auto;
                &:active {
                    color: #fff !important;
                }
            }
            &:active,
            &:hover {
                background: rgb(33, 74, 42);
                border-color: rgb(33, 74, 42);
            }
        `,
    },
    description: {
        css: css`
            white-space: pre-wrap;
            height: auto;
        `,
    },
    productImageWrapper: {
        css: css`
            white-space: pre-wrap;
            height: auto;
            > a {
                width: 50px;
                > div {
                    > p[class^="TypographyStyle"] {
                        width: 50px;
                        height: 50px;
                    }
                    > img {
                        width: 50px;
                        height: 50px;
                    }
                }
            }
        `,
    },
    spareMobileStyleComponent: {
        css: css`
            [class^="GridItemStyle"] {
                padding: 5px 0;
            }
            [class^="GridOffset"] {
                margin: 0;
                width: 100% !important;
            }
            padding: 15px;
            h4 {
                font-weight: bold;
            }
            &:nth-child(odd) {
                background: #f3f3f3;
            }
        `,
    },
    hiddenSpareMobileStyleComponent: {
        css: css`
        width: 100%;
        `,
    }
};

const SparePartProduct: React.FC<Props> = ({
    carouselId,
    product,
    updateProductInfo,
    extendedStyles,
    productInfo,
    productSelectionPartsModel,
}) => {
    const [styles] = React.useState(() => mergeToNew(sparePartProductStyles, extendedStyles));

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    return (
        <>
            <Hidden {...styles.hiddenSpareMobileStyleComponent} above="md">
                <ProductContext.Provider value={productContext}>
                    <StyledWrapper
                        {...styles.spareMobileStyleComponent}
                        key={productSelectionPartsModel?.id}
                        data-test-selector="invoiceHistory_invoiceLine"
                    >
                        <GridContainer>
                            <GridItem width={5} as="h4">
                                {translate("Parts")}
                            </GridItem>
                            <GridItem
                                width={7}
                            >{`${productSelectionPartsModel.partNumber}. ${productSelectionPartsModel.chartPartName}`}</GridItem>
                        </GridContainer>
                        <GridContainer>
                            <GridItem width={5} as="h4">
                                {translate("Image")}
                            </GridItem>
                            <GridItem width={7}>
                                <ProductImage product={productContext} extendedStyles={styles.productImageStyles} />
                            </GridItem>
                        </GridContainer>
                        <GridContainer>
                            <GridItem width={5} as="h4">
                                {translate("MFG Part Number")}
                            </GridItem>
                            <GridItem width={7}>{`${product.brand?.name} ${product.manufacturerItem}`}</GridItem>
                        </GridContainer>
                        <GridContainer>
                            <GridItem width={5} as="h4">
                                {translate("Description")}
                            </GridItem>
                            <GridItem width={7}>{productSelectionPartsModel.chartDescription}</GridItem>
                        </GridContainer>
                        <GridContainer>
                            <GridItem width={5} as="h4">
                                {translate("View Specs & Shop")}
                            </GridItem>
                            <GridItem width={7}>
                                {productContext.productInfo.productDetailPath ? (
                                    <Link
                                        target="_blank"
                                        {...styles.submitButton}
                                        href={productContext.productInfo.productDetailPath}
                                    >
                                        {translate("View Specs & Shop")}
                                    </Link>
                                ) : null}
                            </GridItem>
                        </GridContainer>
                    </StyledWrapper>
                </ProductContext.Provider>
            </Hidden>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(SparePartProduct);
